import { useEffect } from "react";
import mixpanel from "mixpanel-browser";
import { useSetRecoilState } from "recoil";
import { webTestTypeAtom } from "../../recoil/webTestTypeAtom";
import { webFunnelApi } from "../../api/webFunnelApi";
import useMixpanelProfileMkt from "../../hooks/useMixpanelProfileMkt";

const WEB_FUNNEL_DISTINCT_ID_STORAGE_KEY = "WEB_FUNNEL_DISTINCT_ID";
const WEB_FUNNEL_ONBOARDING_TYPE_STORAGE_KEY = "WEB_FUNNEL_ONBOARDING_TYPE";
const DEFAULT_ONBOARDING_TYPE = "d";

export default function PromotionInitializer() {
  const setWebTestType = useSetRecoilState(webTestTypeAtom);
  const setPromotionPeople = useMixpanelProfileMkt("v4");

  useEffect(() => {
    let isMounted = true;

    const persistOnboardingType = (onboardingType: string) => {
      localStorage.setItem(
        WEB_FUNNEL_ONBOARDING_TYPE_STORAGE_KEY,
        onboardingType
      );
    };

    const readStoredOnboardingType = () =>
      localStorage.getItem(WEB_FUNNEL_ONBOARDING_TYPE_STORAGE_KEY) ?? undefined;

    const setPromotionState = (onboardingType: string) => {
      if (!isMounted) return;

      setWebTestType({ onboardingType });
    };

    const syncWebFunnelState = (onboardingType: string) => {
      if (!isMounted) return;

      setPromotionState(onboardingType);
      const mixpanelProperties: { [key: string]: string } = {
        "테스트 유형": "C",
        "로그인 페이지 유형": "D",
        "온보딩 유형": onboardingType.toUpperCase(),
      };

      setPromotionPeople(mixpanelProperties);
    };

    const initPromotionState = async () => {
      const waitForMixpanelAndInit = async (retryCount: number) => {
        if (!isMounted) return;

        // mixpanel.promotion 인스턴스 확인
        const instance = (mixpanel as any).promotion;
        const distinctId = instance?.get_distinct_id?.();

        if (distinctId) {
          // Mixpanel Ready
          instance?.identify?.(distinctId);

          // page-data/save 등 후속 API에서 재사용할 수 있도록 distinctId를 저장
          localStorage.setItem(WEB_FUNNEL_DISTINCT_ID_STORAGE_KEY, distinctId);

          const latestStoredOnboardingType = readStoredOnboardingType();

          try {
            const res = await webFunnelApi.begin(
              distinctId,
              latestStoredOnboardingType
            );
            const nextOnboardingType =
              res.result?.onboardingType ?? DEFAULT_ONBOARDING_TYPE;

            persistOnboardingType(nextOnboardingType);
            syncWebFunnelState(nextOnboardingType);
          } catch (e) {
            console.error("Failed to sync promotion onboarding type", e);
            persistOnboardingType(DEFAULT_ONBOARDING_TYPE);
            syncWebFunnelState(DEFAULT_ONBOARDING_TYPE);
          }
        } else {
          // Retry
          if (retryCount > 0) {
            setTimeout(() => waitForMixpanelAndInit(retryCount - 1), 200);
          } else {
            persistOnboardingType(DEFAULT_ONBOARDING_TYPE);
            syncWebFunnelState(DEFAULT_ONBOARDING_TYPE);
          }
        }
      };

      // Start Polling (max 10s)
      waitForMixpanelAndInit(50);
    };

    initPromotionState();

    return () => {
      isMounted = false;
    };
  }, [setWebTestType, setPromotionPeople]);

  return null;
}
